import { Controller } from "@hotwired/stimulus";
import {
  computePosition,
  offset,
  flip,
  shift,
  size,
  autoUpdate,
} from "@floating-ui/dom";

// Distance between the trigger and the panel on whichever side it opens. The panel's
// CSS `margin-top: 4px` is zeroed while positioned, so this is the whole gap.
const PANEL_GAP = 8;
const VIEWPORT_PADDING = 8;

export default class extends Controller {
  static targets = ["button", "panel", "input", "option", "label", "hidden", "clear"];
  static values = {
    openClass: String,
    multiple: Boolean,
    prompt: String,
    chips: Boolean,
    exclusiveValue: String,
  };

  connect() {
    this.isOpen = false;
    this.highlightIndex = -1;
    this.cleanupFloating = null;
    if (this.multipleValue) {
      this._syncMultipleInputs();
      this._syncMultipleLabel();
    }
    this._updateClearIconVisibility();
    this._bindAutofillReconciliation();
  }

  disconnect() {
    this._unbindAutofillReconciliation();
  }

  // ----- open / close -----

  async open() {
    if (this.isOpen) return;
    this._closeOtherSelects();
    this.isOpen = true;

    this.panelTarget.hidden = false;
    this.buttonTarget.setAttribute("aria-expanded", "true");
    this.element.classList.add(this.openClassValue || "");
    this._scrollListToStart();

    // focus search
    if (this.hasInputTarget) {
      requestAnimationFrame(() => this.inputTarget?.focus());
    }

    // floating-ui positioning
    this.useFixedPanel = this.element.closest(".data-table-wrapper") !== null;
    // Read before any inline maxHeight is applied, so a page's own CSS cap (e.g. 240px
    // on library bundles) stays the ceiling that the available room is measured against.
    this.panelCssMaxHeight = parseFloat(getComputedStyle(this.panelTarget).maxHeight);
    this.cleanupFloating = autoUpdate(
      this.buttonTarget,
      this.panelTarget,
      () => this._positionPanel()
    );

    this._bindOutsideEvents();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    if (this.hasInputTarget) {
      this.inputTarget.value = "";
      this.filter();
    }

    this.panelTarget.hidden = true;
    this.buttonTarget.setAttribute("aria-expanded", "false");
    this.element.classList.remove(this.openClassValue || "");
    this.highlightIndex = -1;
    this._updateHighlighted();

    if (this.cleanupFloating) {
      this.cleanupFloating();
      this.cleanupFloating = null;
    }

    this._resetPanelPosition();
    this._unbindOutsideEvents();
  }

  // Opens below the trigger, flips above it when the room below (viewport or a clipping
  // ancestor such as `.main-content`) is too short, and shrinks to whatever room the
  // chosen side has — so the list is never cut off wherever the field sits on the page.
  _positionPanel() {
    const strategy = this.useFixedPanel ? "fixed" : "absolute";

    return computePosition(this.buttonTarget, this.panelTarget, {
      placement: "bottom-start",
      strategy,
      middleware: [
        offset(PANEL_GAP),
        flip({ padding: VIEWPORT_PADDING }),
        shift({ padding: VIEWPORT_PADDING }),
        size({
          padding: VIEWPORT_PADDING,
          apply: ({ availableHeight, elements }) => {
            elements.floating.style.maxHeight = `${this._panelHeightCap(availableHeight)}px`;
          },
        }),
      ],
    }).then(({ x, y }) => {
      if (this.useFixedPanel) {
        Object.assign(this.panelTarget.style, {
          position: "fixed",
          left: `${x}px`,
          top: `${y}px`,
          marginTop: "0",
          minWidth: `${this.buttonTarget.offsetWidth}px`,
        });
      } else {
        Object.assign(this.panelTarget.style, {
          position: "",
          left: "",
          top: `${y}px`,
          marginTop: "0",
          minWidth: "",
        });
      }
    });
  }

  _panelHeightCap(availableHeight) {
    const room = Math.max(0, availableHeight);
    if (Number.isNaN(this.panelCssMaxHeight)) return room;

    return Math.min(this.panelCssMaxHeight, room);
  }

  _resetPanelPosition() {
    this.panelTarget.style.position = "";
    this.panelTarget.style.left = "";
    this.panelTarget.style.top = "";
    this.panelTarget.style.marginTop = "";
    this.panelTarget.style.minWidth = "";
    this.panelTarget.style.maxHeight = "";
  }

  // A closed panel keeps whatever scroll offset the last open left behind, so the list
  // could reopen mid-alphabet with nothing typed. Start at the top, or at the current
  // selection — scrolling only the panel, never the page or a clipping ancestor.
  _scrollListToStart() {
    this.panelTarget.scrollTop = 0;

    const visibleOptions = this._visibleOptions();
    const selected = visibleOptions.find((option) =>
      option.classList.contains("app-select-option-selected")
    );
    if (!selected) return;

    // Land the selection exactly where the first option sits at scroll 0 — just under
    // the sticky search header, which cannot slide above the panel's own padding.
    this.panelTarget.scrollTop = selected.offsetTop - visibleOptions[0].offsetTop;
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  // ----- outside click / esc -----

  _bindOutsideEvents() {
    this._outsideClickHandler = (event) => {
      if (!this.element.contains(event.target)) this.close();
    };
    this._escHandler = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        this.buttonTarget.focus();
      }
    };
    document.addEventListener("click", this._outsideClickHandler);
    document.addEventListener("keydown", this._escHandler);
  }

  _unbindOutsideEvents() {
    document.removeEventListener("click", this._outsideClickHandler);
    document.removeEventListener("keydown", this._escHandler);
  }

  // ----- selection -----

  selectOption(event) {
    if (event.target.closest(".app-select-option-eye")) return;

    const option = event.currentTarget;

    if (this.multipleValue) {
      this._toggleMultipleOption(option);
      return;
    }

    this._updateValue(option.dataset.value, { ...option.dataset });
    this._renderSelectedLabel(option);
    this._markSelectedOption(option);

    if (this.hasInputTarget) {
      this.inputTarget.value = "";
    }

    this.filter();
    this.close();
    this.buttonTarget.focus();
    this._updateClearIconVisibility();
  }

  selectAll(event) {
    if (event) event.preventDefault();
    if (!this.multipleValue) return;

    this.optionTargets.forEach((option) => {
      if (option.dataset.selectAllSkip === "true") return;
      option.classList.add("app-select-option-selected");
    });

    this._syncMultipleInputs();
    this._syncMultipleLabel();
    this._dispatchChange({ value: this._selectedMultipleValues(), values: this._selectedMultipleValues() });
    this._updateClearIconVisibility();

    if (this.hasInputTarget) this.inputTarget.value = "";
    this.filter();
  }

  clear(event) {
    event.stopPropagation();

    if (this.multipleValue) {
      this.optionTargets.forEach((el) =>
        el.classList.remove("app-select-option-selected")
      );
      this._syncMultipleInputs();
      this._syncMultipleLabel();
      this._dispatchChange({ value: [], values: [] });
      this._updateClearIconVisibility();
      return;
    }

    this._updateValue("");
    if (this.hasLabelTarget) this.labelTarget.textContent = this.promptValue || "Select an option";
    this.optionTargets.forEach((el) =>
      el.classList.remove("app-select-option-selected")
    );
    this._updateClearIconVisibility();
  }

  _updateValue(value, optionData = {}) {
    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = value;
      this._dispatchChange({ ...optionData, value: this.hiddenTarget.value });
    }
  }

  _renderSelectedLabel(option) {
    if (!this.hasLabelTarget) return;

    const { label, tag } = option.dataset;
    if (tag) {
      this.labelTarget.innerHTML = `${this._escapeHtml(label)} <span class="app-select-option-tag">${this._escapeHtml(tag)}</span>`;
    } else {
      this.labelTarget.textContent = label;
    }
  }

  _markSelectedOption(option) {
    this.optionTargets.forEach((el) =>
      el.classList.remove("app-select-option-selected")
    );
    option.classList.add("app-select-option-selected");
  }

  // ----- browser autofill -----

  // Browser address autofill writes straight into the hidden input that carries
  // this select's form value, and fills it with the human-readable label
  // ("United States") rather than the option value ("US"). Map it back to the
  // real value before it is submitted, and resync the button so what the payer
  // sees matches what is sent.
  _bindAutofillReconciliation() {
    if (this.multipleValue || !this.hasHiddenTarget) return;

    this._reconcileAutofilledValue = this._reconcileAutofilledValue.bind(this);
    this.autofilledInput = this.hiddenTarget;
    this.autofilledForm = this.element.closest("form");

    this.autofilledInput.addEventListener("input", this._reconcileAutofilledValue);
    this.autofilledInput.addEventListener("change", this._reconcileAutofilledValue);
    // Capture phase: autofill does not reliably fire input/change, so this is
    // the last chance to correct the value before a submit handler reads it.
    this.autofilledForm?.addEventListener("submit", this._reconcileAutofilledValue, true);
  }

  _unbindAutofillReconciliation() {
    if (!this.autofilledInput) return;

    this.autofilledInput.removeEventListener("input", this._reconcileAutofilledValue);
    this.autofilledInput.removeEventListener("change", this._reconcileAutofilledValue);
    this.autofilledForm?.removeEventListener("submit", this._reconcileAutofilledValue, true);
    this.autofilledInput = null;
    this.autofilledForm = null;
  }

  _reconcileAutofilledValue() {
    const submitted = this.autofilledInput.value.trim();
    if (!submitted) return;

    // An unrecognized value is left alone: clearing it would trip `required` on
    // a zero-size input the browser cannot focus, blocking the form with no
    // visible reason. The server rejects it with a message instead.
    const option = this._optionMatching(submitted);
    if (!option) return;

    this.autofilledInput.value = option.dataset.value;
    if (option.classList.contains("app-select-option-selected")) return;

    this._renderSelectedLabel(option);
    this._markSelectedOption(option);
    this._updateClearIconVisibility();
  }

  _optionMatching(value) {
    const normalized = value.toLowerCase();

    return (
      this.optionTargets.find((option) => option.dataset.value === value) ||
      this.optionTargets.find((option) => option.dataset.value.toLowerCase() === normalized) ||
      this.optionTargets.find((option) => option.dataset.label.toLowerCase() === normalized)
    );
  }

  _toggleMultipleOption(option) {
    const exclusive = this.hasExclusiveValueValue && this.exclusiveValueValue;
    const isExclusive = exclusive && option.dataset.value === exclusive;
    const willSelect = !option.classList.contains("app-select-option-selected");

    if (willSelect && isExclusive) {
      this.optionTargets.forEach((item) => item.classList.remove("app-select-option-selected"));
    } else if (willSelect && exclusive) {
      this.optionTargets
        .filter((item) => item.dataset.value === exclusive)
        .forEach((item) => item.classList.remove("app-select-option-selected"));
    }

    option.classList.toggle("app-select-option-selected");
    this._syncMultipleInputs();
    this._syncMultipleLabel();
    this._dispatchChange({ ...option.dataset, value: this._selectedMultipleValues(), values: this._selectedMultipleValues() });
    this._updateClearIconVisibility();

    if (this.hasInputTarget) this.inputTarget.value = "";
    this.filter();
    option.focus();
  }

  _syncMultipleInputs() {
    if (!this.hasHiddenTarget) return;

    const selectedValues = this._selectedMultipleValues();

    if (this.hiddenTarget.tagName === "SELECT") {
      Array.from(this.hiddenTarget.options).forEach((option) => {
        const selected = selectedValues.includes(option.value);
        option.selected = selected;
        option.defaultSelected = selected;
      });
      this._syncMultipleHiddenInputs(selectedValues);
      return;
    }

    const name = this.hiddenTarget.name;
    this.hiddenTarget.value = "";
    this.hiddenTargets.slice(1).forEach((input) => input.remove());

    let anchor = this.hiddenTarget;
    selectedValues.forEach((value) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.className = "select-hidden-input-value";
      input.name = name;
      input.value = value;
      input.dataset.appSelectTarget = "hidden";
      input.autocomplete = "custom_select_input";
      anchor.insertAdjacentElement("afterend", input);
      anchor = input;
    });
  }

  // Zero-size <select multiple> is not a reliable successful control in every
  // driver. Mirror the selection onto hidden inputs so the form posts the
  // values even when the select itself is omitted.
  _syncMultipleHiddenInputs(selectedValues) {
    const name = this.hiddenTarget.name;
    this.element.querySelectorAll("input.select-hidden-synced").forEach((input) => input.remove());

    let anchor = this.hiddenTarget;
    selectedValues.forEach((value) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.className = "select-hidden-input-value select-hidden-synced";
      input.name = name;
      input.value = value;
      input.autocomplete = "custom_select_input";
      anchor.insertAdjacentElement("afterend", input);
      anchor = input;
    });
  }

  _closeOtherSelects() {
    this.application.controllers.forEach((controller) => {
      if (controller.identifier === "app-select" && controller !== this && controller.isOpen) {
        controller.close();
      }
    });
  }

  refreshLabel() {
    if (this.multipleValue) this._syncMultipleLabel();
  }

  removeChip(event) {
    event.preventDefault();
    event.stopPropagation();
    const value = event.currentTarget.dataset.value;
    const option = this.optionTargets.find((item) => item.dataset.value === value);
    if (option) this._toggleMultipleOption(option);
  }

  _syncMultipleLabel() {
    if (!this.hasLabelTarget) return;

    const selected = this.optionTargets.filter((option) =>
      option.classList.contains("app-select-option-selected")
    );

    if (!selected.length) {
      this.labelTarget.textContent = this.promptValue || "Select an option";
      return;
    }

    if (!this.chipsValue) {
      this.labelTarget.textContent = selected.map((option) => option.dataset.label).join(", ");
      return;
    }

    this.labelTarget.replaceChildren();
    selected.forEach((option) => {
      const chip = document.createElement("span");
      chip.className = "app-select-chip";

      const text = document.createElement("span");
      text.className = "app-select-chip-label";
      text.textContent = option.dataset.label;
      chip.appendChild(text);

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "app-select-chip-remove";
      remove.setAttribute("aria-label", `Remove ${option.dataset.label}`);
      remove.dataset.value = option.dataset.value;
      remove.addEventListener("click", (event) => this.removeChip(event));
      remove.innerHTML = '<i class="ph ph-x" aria-hidden="true"></i>';
      chip.appendChild(remove);

      this.labelTarget.appendChild(chip);
    });
  }

  selectedValues() {
    if (this.multipleValue) return this._selectedMultipleValues();
    if (!this.hasHiddenTarget || !this.hiddenTarget.value) return [];

    return [this.hiddenTarget.value];
  }

  // Replay a prior multi-select snapshot without dispatching change. Session
  // builder uses this when a save is refused so the control matches the server.
  restoreMultipleSelection(values) {
    if (!this.multipleValue) return;

    const selected = new Set((Array.isArray(values) ? values : []).map(String));
    this.optionTargets.forEach((option) => {
      option.classList.toggle("app-select-option-selected", selected.has(String(option.dataset.value)));
    });
    this._syncMultipleInputs();
    this._syncMultipleLabel();
    this._updateClearIconVisibility();
  }

  _selectedMultipleValues() {
    return this.optionTargets
      .filter((option) => option.classList.contains("app-select-option-selected"))
      .map((option) => option.dataset.value);
  }

  _dispatchChange(detail = {}) {
    if (!this.hasHiddenTarget) return;

    this.hiddenTarget.dispatchEvent(
      new CustomEvent('app-select:change', {
        bubbles: true,
        detail
      })
    );
  }

  _updateClearIconVisibility() {
    if (!this.hasClearTarget) return;
    const hasValue = this.multipleValue ? this._selectedMultipleValues().length > 0 : this.hasHiddenTarget && this.hiddenTarget.value;
    this.clearTarget.classList.toggle("d-none", !hasValue);
  }

  // ----- filtering -----

  filter() {
    let query = "";
    if (this.hasInputTarget) {
      query = this.inputTarget.value.trim().toLowerCase();
    }

    this.optionTargets.forEach((el) => {
      if (el.dataset.disabledByStage === 'true' || el.dataset.keepHidden === 'true') {
        el.hidden = true;
        return;
      }
      const label = el.dataset.label.toLowerCase();
      const matches = label.includes(query);
      el.hidden = !matches;
    });

    this.highlightIndex = -1;
    this._updateHighlighted();
  }

  // ----- keyboard handling -----

  buttonKeydown(event) {
    if (!["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) return;

    event.preventDefault();
    if (!this.isOpen) this.open();

    if (["ArrowDown", "ArrowUp"].includes(event.key)) {
      this._moveHighlight(event.key === "ArrowDown" ? 1 : -1);
    }
  }

  listKeydown(event) {
    if (!this.isOpen) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this._moveHighlight(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this._moveHighlight(-1);
        break;
      case "Enter":
        event.preventDefault();
        if (this.highlightIndex >= 0) {
          this.optionTargets[this.highlightIndex].click();
        }
        break;
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        this.close();
        this.buttonTarget.focus();
        break;
      case "Tab":
        this.close();
        break;
    }
  }

  _visibleOptions() {
    return this.optionTargets.filter((el) => !el.hidden);
  }

  _moveHighlight(delta) {
    const visible = this._visibleOptions();
    if (!visible.length) return;

    // map current optionTargets index → visible index
    const currentVisibleIndex =
      this.highlightIndex >= 0
        ? visible.indexOf(this.optionTargets[this.highlightIndex])
        : -1;

    let nextVisibleIndex = currentVisibleIndex + delta;
    if (nextVisibleIndex < 0) nextVisibleIndex = visible.length - 1;
    if (nextVisibleIndex >= visible.length) nextVisibleIndex = 0;

    const nextOption = visible[nextVisibleIndex];
    this.highlightIndex = this.optionTargets.indexOf(nextOption);
    this._updateHighlighted();
    nextOption.focus();
  }

  _updateHighlighted() {
    this.optionTargets.forEach((el, index) => {
      if (index === this.highlightIndex) {
        el.classList.add("app-select-option-active");
      } else {
        el.classList.remove("app-select-option-active");
      }
    });
  }

  _escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
